import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";
import { getSingleSale } from "../../services/services";
import Loading from "../../components/loading";
import moment from "moment";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const SaleInvoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sale, setSale] = useState(null);
  const printRef = useRef();
  const [loading, setLoading] = useState(true);
  const { activeBusiness, list } = useSelector((state) => state.business);
  const selectedBusiness =
    list.find((biz) => biz._id === sale?.businessId) || activeBusiness;

  const [returns, setReturns] = useState([]);

  useEffect(() => {
    const fetchSale = async () => {
      try {
        const res = await getSingleSale(id);
        setSale(res.data.response?.singleSale);
        setReturns(res.data.response?.returns || []);
      } catch (err) {
        console.error("Error fetching sale:", err);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchSale();
  }, [id]);

  const numberToWords = (num) => {
    if (num === 0) return "Zero";
    const ones = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const tens = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];
    const scales = ["", "Thousand", "Lakh", "Crore"];

    const helper = (n) => {
      if (n < 20) return ones[n];
      if (n < 100)
        return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
      if (n < 1000)
        return (
          ones[Math.floor(n / 100)] +
          " Hundred" +
          (n % 100 ? " " + helper(n % 100) : "")
        );
      for (let i = scales.length - 1; i > 0; i--) {
        const scaleVal = i === 3 ? 10000000 : i === 2 ? 100000 : 1000;
        if (n >= scaleVal) {
          return (
            helper(Math.floor(n / scaleVal)) +
            " " +
            scales[i] +
            (n % scaleVal ? " " + helper(n % scaleVal) : "")
          );
        }
      }
      return "";
    };
    return helper(Math.round(num)).trim();
  };

  const amountInWords = (amount) => `${numberToWords(amount)}`;

  if (loading) return <Loading />;
  if (!sale)
    return (
      <div className="text-center mt-5 py-5 text-danger fs-4">
        Invoice Not Found!
      </div>
    );

  const invoiceType = sale.invoiceType || "taxInvoice";
  const config = {
    taxInvoice: { title: "Tax Invoice", showGST: true },
    billOfSupply: { title: "BILL OF  SUPPLY", showGST: false },
    deliveryChallan: { title: "DELIVERY CHALLAN", showGST: false },
    proformaInvoice: { title: "PROFORMA INVOICE", showGST: true },
  };
  const { title, showGST } = config[invoiceType] || config.taxInvoice;

  const allLines = [];

  // ITEMS
  sale.items?.forEach((item) => {
    allLines.push({
      description: item.itemName,
      qty: item.sellingQty,
      rate: item.sellingPrice,
      gstRate: item.gstRate || 0,
      gstAmount: item.gstAmount || 0,
      total: item.totalWithTax || 0,
      unit: item.unit?.shortName || item.unit?.name || "",
    });
  });

  // SERVICES
  sale.services?.forEach((s) => {
    allLines.push({
      description: (
        <>
          <div>
            <b>{s.name} (Service)</b>
          </div>
          {s.details && (
            <div style={{ fontSize: "11px", color: "#555" }}>{s.details}</div>
          )}
        </>
      ),

      qty: s.qty,
      rate: s.baseCost,
      gstRate: s.gstRate || 0,
      gstAmount: s.gstAmount || 0,
      total: s.total || 0,
    });
  });

  // OTHER EXPENSES
  sale.otherExpenses?.forEach((exp) => {
    allLines.push({
      description: `${exp.name} (Expense)`,
      qty: 1,
      rate: exp.price,
      gstRate: exp.gstRate || 0,
      gstAmount: exp.gstAmount || 0,
      total: exp.totalWithTax || exp.price,
    });
  });
  const subtotal = sale.totalBeforeDiscount || 0;
  const discount = sale.discountAmount || sale.discountValue || 0;
  const grandTotal = sale.totalAmount || 0;

  const totalWithoutGST = allLines.reduce(
    (sum, line) => sum + line.qty * line.rate,
    0,
  );

  const totalGST = allLines.reduce(
    (sum, line) => sum + (line.gstAmount || 0),
    0,
  );

  const handlePrint = () => {
    window.print();
  };
  const totalReturned = returns.reduce(
    (sum, item) => sum + (item.totalReturnAmount || 0),
    0,
  );
  const finalAmount = grandTotal - totalReturned;
  const balanceDue = Math.max(0, finalAmount - (sale.receivedAmount || 0));
  return (
    <div id="main">
      {/* HEADER */}
      <div
        className="no-print"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "10px",
          paddingTop: "4px",
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <i
            className="bi bi-arrow-left"
            style={{ cursor: "pointer", fontSize: "20px" }}
            onClick={() => navigate(-1)}
          ></i>

          <h6 className="m-0 fw-bold">
            {title} - #{sale.invoiceNumber}
          </h6>
        </div>

        <button onClick={handlePrint} className="btn btn-primary">
          {/* Print / Save PDF */}
          <span className="d-block">Print/Save</span>
          <span className="d-block"> PDF</span>
        </button>
      </div>

      <div ref={printRef} className="invoice-a4">
        <style>{`
              @page {
                size: A4;
                margin: 0;
              }

              body {
                margin: 0;
                color: black;
              }

              .invoice-a4 {
                width: 100%; 
                padding: 4mm; 
                margin: 0 auto;
                box-sizing: border-box;
                font-family: Poppins, sans-serif;
                font-size: 12px;
                background: #fff;
              }

              table {
                width: 100%;
                border-collapse: collapse;
              }

              th, td {
                border: 1px solid #ccc;
                padding: 6px;
              }

              th {
                background: #f5f5f5;
              }

              .right { text-align: right; }
              .center { text-align: center; }
              .bold { font-weight: bold; }

              @media print {
                body * {
                  visibility: hidden !important;
                }

                .invoice-a4,
                .invoice-a4 * {
                  visibility: visible !important;
                }

                .invoice-a4 {
                  position: absolute;
                  left: 0;
                  top: 0;
                  page-break-after: avoid;
                  min-height: 297mm;
                }

                table, tr, td, th {
                  page-break-inside: avoid !important;
                }

                .no-print {
                  display: none !important;
                }
              }

              @media screen and (max-width: 768px) {
              .invoice-a4 {
                width: 100%;
                min-height: auto;
                padding: 12px;
                font-size: 11px;
              }

              table {
                font-size: 11px;
              }
              }
        `}</style>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "20px",
          }}
        >
          <div
            style={{
              width: "65%",
              wordWrap: "break-word",
              overflowWrap: "break-word",
            }}
          >
            {selectedBusiness?.logo &&
              selectedBusiness.logo !== "null" &&
              selectedBusiness.logo !== "undefined" &&
              selectedBusiness.logo.trim() !== "" && (
                <img
                  src={`${process.env.REACT_APP_IMAGE_URL}/uploads/business/${selectedBusiness.logo}`}
                  alt="logo"
                  style={{ maxHeight: 30 }}
                  onError={(e) => {
                    e.currentTarget.style.display = "none"; // hide if image fails to load
                  }}
                />
              )}
            <h6 style={{ fontWeight: 700 }} className="mt-2 mb-0">
              {selectedBusiness?.businessName}
            </h6>
            <div style={{ whiteSpace: "normal" }}>
              {selectedBusiness?.address}
            </div>
            <div>Phone: {selectedBusiness?.phone}</div>
            {selectedBusiness?.gstNumber && (
              <div>GSTIN: {selectedBusiness.gstNumber}</div>
            )}
            {selectedBusiness?.state && (
              <div style={{ textTransform: "capitalize" }}>
                State: {selectedBusiness.state}
              </div>
            )}
            {selectedBusiness?.pin && (
              <div>Pin code: {selectedBusiness.pin}</div>
            )}
          </div>

          <div style={{ textAlign: "right", style: "35%" }}>
            <h5 style={{ marginBottom: "8px" }}>{title}</h5>

            <div>
              <div className="mb-1">
                <b>Invoice No:</b> {sale.invoiceNumber}
              </div>

              <div>
                <b>Date:</b> {moment(sale.saleDate).format("DD MMM, YYYY")}
              </div>
            </div>
          </div>
        </div>

        <hr />

        {/* CUSTOMER */}
        <div>
          <b>Bill To:</b>
          <div>{sale.customerId?.name}</div>
          <div>
            {sale.customerId?.billingAddress?.map((addr) => (
              <div key={addr._id}>{addr.address}</div>
            ))}
          </div>

          {sale.customerId?.gstin && <div>GSTIN: {sale.customerId.gstin}</div>}
        </div>

        <br />

        {/* ITEMS */}
        <table className="table-responsive">
          <thead>
            <tr>
              <th className="center">S.No.</th>
              <th className="center">Item</th>
              <th className="center">Rate (unit)</th>
              <th className="center">Qty</th>
              <th className="center">Total Price (₹)</th>
              <th className="center">GST %</th>
              <th className="center">Total GST (₹)</th>
              <th className="center"> Total Amount (With GST %)</th>
            </tr>
          </thead>
          <tbody>
            {allLines.map((line, i) => (
              <tr key={i}>
                <td className="center">{i + 1}</td>
                <td className="center">{line.description}</td>
                <td className="center">{line.rate.toFixed(2)}</td>
                <td className="center">{`${line.qty || 1}`}</td>
                <td className="center">{(line.qty * line.rate).toFixed(2)}</td>
                <td className="center">{line.gstRate.toFixed(2)}</td>
                <td className="center">{line.gstAmount.toFixed(2)}</td>
                <td className="center">{line.total.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {returns.length > 0 && (
          <>
            <br />
            <h6>Returned Items</h6>

            <table>
              <thead>
                <tr>
                  <th className="center">S.No.</th>
                  <th className="center">Item</th>
                  <th className="center">Qty</th>
                  {/* <th className="center">Rate</th> */}
                  <th className="center">Total Rate</th>

                  <th className="center">Total GST %(₹)</th>
                  <th className="center">Total Amount (With GST %)</th>
                  <th className="center">Reason</th>
                </tr>
              </thead>

              <tbody>
                {returns.map((ret, index) => (
                  <tr key={ret._id}>
                    <td className="center">{index + 1}</td>

                    <td className="center">{ret.product?.name}</td>

                    <td className="center">{ret.quantity}</td>

                    <td className="center">
                      {ret.returnItemAmount?.toFixed(2)}
                    </td>

                    <td className="center">{ret.gstAmount.toFixed(2)}</td>

                    <td className="center">
                      {ret.totalReturnAmount?.toFixed(2)}
                    </td>

                    <td className="center">{ret.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        <br />

        {/* TOTALS */}
        <table>
          <tbody>
            {totalWithoutGST > 0 && (
              <tr>
                <td className="right">Sub Total</td>
                <td className="right">₹ {totalWithoutGST.toFixed(2)}</td>
              </tr>
            )}

            {totalGST > 0 && (
              <tr>
                <td className="right">Total GST</td>
                <td className="right">₹ {totalGST.toFixed(2)}</td>
              </tr>
            )}

            {discount > 0 && (
              <tr>
                <td className="right">
                  Discount {sale.discountType === "percent" ? "(%)" : ""}
                </td>
                <td className="right">- ₹ {discount.toFixed(2)}</td>
              </tr>
            )}

            <tr>
              <td className="right bold"> Grand Total</td>
              <td className="right bold">₹ {grandTotal.toFixed(2)}</td>
            </tr>

            {sale.receivedAmount > 0 && (
              <tr>
                <td className="right">Paid</td>
                <td className="right">₹ {sale.receivedAmount.toFixed(2)}</td>
              </tr>
            )}

            {totalReturned > 0 && (
              <tr>
                <td className="right">Return</td>
                <td className="right">₹ {totalReturned.toFixed(2)}</td>
              </tr>
            )}

            {/* {sale.dueAmount > 0 && (
              <tr>
                <td className="right bold">Due</td>
                <td className="right bold">₹ {sale.dueAmount.toFixed(2)}</td>
              </tr>
            )}
            {sale.finalAmount > 0 && (
              <tr>
                <td className="right bold">Final Amount</td>
                <td className="right bold">₹ {sale.finalAmount.toFixed(2)}</td>
              </tr>
            )} */}

            <tr>
              <td className="right bold">Final Amount</td>
              <td className="right bold">₹ {finalAmount.toFixed(2)}</td>
            </tr>
            {sale.dueAmount > 0 && (
              <tr>
                <td className="right bold">Balance Due</td>
                <td className="right bold">₹ {balanceDue.toFixed(2)}</td>
              </tr>
            )}
          </tbody>
        </table>

        <br />

        {/* AMOUNT IN WORDS */}
        {/* <div>
          <b>Amount in Words:</b> {amountInWords(Math.round(grandTotal))} Rupees
          Only
        </div> */}

        <div>
          <b>Amount in Words:</b> {amountInWords(Math.round(finalAmount))}{" "}
          Rupees Only
        </div>

        <div
          style={{
            display: "flex",
            gap: "2px",
          }}
        >
          <span>
            <b>Payment Mode:</b>
          </span>
          <span style={{ textTransform: "capitalize" }}>
            {sale?.paymentMode || "N/A"}
          </span>
        </div>

        <br />

        {/* Buyer + Transport */}
        {sale.transportation && (
          <table style={{ marginBottom: "8px" }}>
            <tbody>
              <tr>
                <td style={{ width: "50%", verticalAlign: "top" }}>
                  <strong>Transport Details</strong>
                  <br />
                  Mode: {sale.transportation?.mode || "-"}
                  <br />
                  Transporter: {sale.transportation?.transporterName || "-"}
                  <br />
                  Vehicle No: {sale.transportation?.vehicleNo || "-"}
                  <br />
                  LR No: {sale.transportation?.lrNo || "-"}
                  <br />
                  Shipping Address:{" "}
                  {sale.transportation?.shippingAddress?.[0]?.address || "-"}
                </td>
              </tr>
            </tbody>
          </table>
        )}

        <br />

        {/* FOOTER */}
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div>
            <b>Declaration</b>
            <div>This is a computer generated invoice.</div>

            {/* TERMS & CONDITIONS */}
            <div
              style={{
                padding: "8px",
                marginTop: "15px",
                fontSize: "10px",
                width: "100%",
                boxSizing: "border-box",
                pageBreakInside: "avoid",
              }}
            >
              <h6
                style={{
                  color: "green",
                  fontSize: "13px",
                  fontWeight: "bold",
                  marginBottom: "6px",
                }}
              >
                Terms & Conditions
              </h6>

              <ul style={{ margin: 0, paddingLeft: "16px" }}>
                <li>
                  <b>Payment Terms:</b> Payment must be made according to the
                  invoice details.
                </li>
                <li>
                  <b>Product Returns:</b> Returned products will be recorded,
                  and the return amount will be adjusted accordingly.
                </li>
                <li>
                  <b>Refund Policy:</b> Eligible refunds will be settled
                  according to the seller's refund policy.
                </li>
                <li>
                  <b>Taxes & GST:</b> All applicable taxes and GST will be
                  charged as per government rules.
                </li>
              </ul>
            </div>
            {/* THANK YOU */}
            {/* <div
              style={{
                textAlign: "center",
                marginTop: "10px",
                paddingTop: "6px",
                borderTop: "2px solid #86b98a",
                fontSize: "13px",
                fontWeight: "500",
              }}
            > */}
          </div>

          {selectedBusiness?.signature &&
            selectedBusiness.signature !== "null" &&
            selectedBusiness.signature !== "undefined" &&
            selectedBusiness.signature.trim() !== "" && (
              <div style={{ textAlign: "center" }}>
                <b>For {selectedBusiness?.businessName}</b>
                <br />
                <img
                  src={`${process.env.REACT_APP_IMAGE_URL}/uploads/business/${selectedBusiness.signature}`}
                  alt="signature"
                  height={60}
                  width={"100%"}
                  onError={(e) => {
                    e.currentTarget.style.display = "none"; // hide if image fails to load
                  }}
                />
              </div>
            )}
        </div>
        <div
          style={{
            textAlign: "center",
            marginTop: "10px",
            paddingTop: "6px",
            borderTop: "2px solid #86b98a",
            fontSize: "13px",
            fontWeight: "500",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          Thank You
        </div>
      </div>
    </div>
  );
};

export default headerLayoutWrapper(SaleInvoice);
